import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Eye, EyeOff } from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import {
  signupValidationSchema,
  signupInitialValues,
  SignupFormValues,
} from '@/pages/Registration/validation'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux/store'
import { userRegister } from '@/redux/asyncThunks/user'
import { useAppSelector } from '@/redux/hooks'
import { resetError } from '@/redux/slices/userSlice'
import ROUTES from '@/routes/routes'

const SignUp: React.FC = () => {
  const [showPassword1, setShowPassword1] = useState<boolean>(false)
  const [showPassword2, setShowPassword2] = useState<boolean>(false)
  const { loading, error } = useAppSelector((state) => state.user)

  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    dispatch(resetError())
  }, [dispatch])

  const handleSubmit = async (values: SignupFormValues) => {
    const formData = {
      name: values.name,
      email: values.email,
      password1: values.password1,
      password2: values.password2,
      role: values.role,
    }

    try {
      const resultAction = await dispatch(userRegister(formData))
      if (userRegister.rejected.match(resultAction)) {
        return
      }
      navigate(ROUTES.VERIFICATION.EMAIL, {
        state: { email: values.email, password: values.password1 },
      })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div className='flex h-screen w-screen flex-col md:flex-row'>
      <div className='relative hidden flex-1 md:block'>
        <img
          src='/images/auth-background.svg'
          alt='Auth background'
          className='absolute w-1/2 object-cover md:h-full md:w-5/6'
        />
        <div className='flex h-full items-center justify-center'>
          <img
            src='/images/auth-avatar.svg'
            alt='Auth Avatar'
            className='absolute z-10 object-fill md:h-full md:w-auto'
          />
        </div>
      </div>
      <div className='flex flex-1 flex-col items-center justify-center'>
        <div className='flex justify-center'>
          <img
            src='/images/oge.svg'
            alt='Huble logo'
            className='w-1/2 object-contain md:w-full'
          />
        </div>
        <div className='flex w-3/4 flex-col items-center md:w-full'>
          <Card className='w-full max-w-md border-none shadow-none'>
            <CardHeader className='space-y-1 flex flex-col items-center'>
              <CardTitle className='text-4xl font-bold'>
                Welcome to OGE
              </CardTitle>
              <CardTitle className='text-2xl text-blue-900'>Sign Up</CardTitle>
            </CardHeader>
            <CardContent>
              <Formik
                initialValues={signupInitialValues}
                validationSchema={signupValidationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  isSubmitting,
                }) => (
                  <Form className='space-y-4'>
                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.name && errors.name
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='name'
                      >
                        Name
                      </Label>
                      <Input
                        className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                          touched.name && errors.name
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                        id='name'
                        name='name'
                        type='text'
                        placeholder='Enter your name'
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.name && errors.name && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.name}
                        </div>
                      )}
                    </div>
                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.email && errors.email
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='email'
                      >
                        Email
                      </Label>
                      <Input
                        className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                          touched.email && errors.email
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                        id='email'
                        name='email'
                        type='email'
                        placeholder='Enter an Email'
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.email && errors.email && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.email}
                        </div>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.password1 && errors.password1
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='password1'
                      >
                        Password
                      </Label>
                      <div className='relative'>
                        <Input
                          className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                            touched.password1 && errors.password1
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                          id='password1'
                          name='password1'
                          type={showPassword1 ? 'text' : 'password'}
                          placeholder='At least 8 characters'
                          value={values.password1}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                          onClick={() => setShowPassword1(!showPassword1)}
                        >
                          {showPassword1 ? (
                            <Eye className='h-4 w-4 text-gray-400' />
                          ) : (
                            <EyeOff className='h-4 w-4 text-gray-400' />
                          )}
                        </Button>
                      </div>
                      {touched.password1 && errors.password1 && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.password1}
                        </div>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.password2 && errors.password2
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='password2'
                      >
                        Confirm Password
                      </Label>
                      <div className='relative'>
                        <Input
                          className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                            touched.password2 && errors.password2
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                          id='password2'
                          name='password2'
                          type={showPassword2 ? 'text' : 'password'}
                          placeholder='At least 8 characters'
                          value={values.password2}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                          onClick={() => setShowPassword2(!showPassword2)}
                        >
                          {showPassword2 ? (
                            <Eye className='h-4 w-4 text-gray-400' />
                          ) : (
                            <EyeOff className='h-4 w-4 text-gray-400' />
                          )}
                        </Button>
                      </div>
                      {touched.password2 && errors.password2 && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.password2}
                        </div>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.role && errors.role
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='role'
                      >
                        Role
                      </Label>
                      <Select
                        onValueChange={(value) =>
                          handleChange({
                            target: {
                              name: 'role',
                              value,
                            },
                          })
                        }
                        value={values.role}
                      >
                        <SelectTrigger
                          className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                            touched.role && errors.role
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        >
                          <SelectValue placeholder='Select Role' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value='professor'>
                              <div className='flex items-center gap-2'>
                                <img
                                  src='/icons/professor.svg'
                                  alt='Professor icon'
                                  className='w-4 h-5'
                                />
                                <p className='text-xs font-normal'>Professor</p>
                              </div>
                            </SelectItem>
                            <SelectItem value='student'>
                              <div className='flex items-center gap-2'>
                                <img
                                  src='/icons/student.svg'
                                  alt='Student icon'
                                  className='w-4 h-5'
                                />
                                <p className='text-xs'>Student</p>
                              </div>
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {touched.role && errors.role && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.role}
                        </div>
                      )}
                    </div>
                    {error && (
                      <div className='text-red-500 text-sm flex gap-2'>
                        <img src='/icons/alert-circle.svg' alt='Warning' />
                        {typeof error == 'object' ? (
                          <>
                            {Object.values(error).map((_error, index) => (
                              <p key={index}>{_error as string}</p>
                            ))}
                          </>
                        ) : (
                          error
                        )}
                      </div>
                    )}
                    <Button
                      type='submit'
                      disabled={loading || isSubmitting}
                      className='w-full h-[38px] text-base font-normal bg-dark-blue hover:bg-dark-blue-hovered'
                    >
                      {loading ? 'Signing up...' : 'Sign Up'}
                    </Button>

                    <div className='text-sm text-center'>
                      <span className='text-[#122B31] text-lg'>
                        Already have an account?{' '}
                      </span>
                      <Link
                        to='/login'
                        className='text-lg text-[#122B31] hover:text-blue-800'
                      >
                        Login
                      </Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default SignUp
